import React, { useEffect, useMemo, useRef, useState } from 'react'
import SimpleModal from 'components/SimpleModal'
import { CustomButton, SimpleTitleCard } from 'styles/global'
import {
  ModalContent,
  Section,
  SectionTitle,
  TagsWrapper,
  TagsLabel,
  Tag,
  HistoricoWrapper,
  InfoArea,
  MetaInfo,
  Title,
  CardBadge,
  LeadHeader,
  LeadInfo,
  LeadName,
  LeadPhone,
  Divider,
  InfoGridTwoCol,
  InfoField,
  InfoFieldLabel,
  InfoFieldValue,
  ActionButtonsRow,
  LeadAvatar,
  LeadHeaderActions,
  LeadHeaderSelect,
  EtapaSelectContainer,
  EtapaSelectLabel,
  EtapaSelectTrigger,
  EtapaSelectValue,
  EtapaSelectMenu,
  EtapaSelectOption
} from '../style'
import { useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import { KANBAN } from 'store/modules/kanban/types'
import { maskCPF, maskPhoneWithDDD } from 'util/masks'
import { Collapse } from 'reactstrap'
import CustomSelect from 'components/Select'
import useRedux from 'hooks/useRedux'
import * as kanbanActions from 'store/modules/kanban/actions'
import * as atendimentoActions from 'store/modules/atendimento/actions'
import InputCustom from 'components/Inputs'
import moment from 'moment'
import {
  BiCheck,
  BiChevronDown,
  BiChevronUp,
  BiTransferAlt
} from 'react-icons/bi'
import { toast } from 'react-toastify'
import {
  BsTelephone,
  BsWhatsapp,
  BsEnvelope,
  BsCalendarCheck
} from 'react-icons/bs'

interface ModalCardProps {
  isOpen: boolean
  onToggle: () => void
}

const ModalCard = ({ isOpen, onToggle }: ModalCardProps) => {
  const { modalKanban, optionsSelect, loadingSelect, kanbanData } = useSelector<
    ApplicationState,
    KANBAN
  >((state) => state.kanban)

  const { dispatch } = useRedux()

  const [invalidAnotacao, setInvalidAnotacao] = useState(false)
  const [interacoesIsOpen, setInteracoesIsOpen] = useState(false)
  const [anotacoesIsOpen, setAnotacoesIsOpen] = useState(false)
  const [isEtapaMenuOpen, setIsEtapaMenuOpen] = useState(false)
  const etapaMenuRef = useRef<HTMLDivElement | null>(null)

  const etapaAtual = useMemo(() => {
    const pipelineEtapaClienteId =
      modalKanban.informacoes.pipelineEtapaClienteId
    if (!pipelineEtapaClienteId) return null

    for (const column of kanbanData.columns) {
      const cardIndex = column.cards.findIndex(
        (card) => String(card.id) === String(pipelineEtapaClienteId)
      )

      if (cardIndex !== -1) {
        return {
          column,
          cardIndex,
          card: column.cards[cardIndex]
        }
      }
    }

    return null
  }, [kanbanData.columns, modalKanban.informacoes.pipelineEtapaClienteId])

  const onChangeEtapa = (pipelineEtapaDestinoId: string | null) => {
    if (!pipelineEtapaDestinoId || !etapaAtual) return

    const origemColumn = etapaAtual.column
    const cardAtual = etapaAtual.card
    if (!origemColumn || !cardAtual) return

    if (String(origemColumn.id) === String(pipelineEtapaDestinoId)) return

    const transicaoPermitida = origemColumn.etapasPermitidas?.some(
      (etapa) =>
        String(
          (etapa as { etapaId?: string; id?: string }).etapaId ||
            (etapa as { etapaId?: string; id?: string }).id
        ) === String(pipelineEtapaDestinoId)
    )

    if (!transicaoPermitida) {
      toast.warning('Movimentação não permitida para a etapa selecionada.')
      return
    }

    const destinoColumn = kanbanData.columns.find(
      (column) => String(column.id) === String(pipelineEtapaDestinoId)
    )

    if (!destinoColumn) return

    dispatch(
      kanbanActions.verificarMotivoMovimentacaoSaga({
        card: cardAtual,
        from: {
          fromPosition: etapaAtual.cardIndex,
          fromColumnId: String(origemColumn.id)
        },
        to: {
          toPosition: destinoColumn.cards.length + 1,
          toColumnId: String(destinoColumn.id)
        }
      })
    )
  }

  const getInitials = (nomeConsultor = '') => {
    return nomeConsultor
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join('')
  }

  const onChangeAnotacao = (value: string) => {
    dispatch(kanbanActions.setAnotacaoCardKanban(value))
  }

  const criarAnotacao = () => {
    if (!modalKanban.anotacao.trim()) {
      setInvalidAnotacao(true)
      return
    } else {
      setInvalidAnotacao(false)
      dispatch(
        kanbanActions.criarAnotacaoCardKanbanSaga(
          modalKanban.informacoes.pipelineEtapaClienteId
        )
      )
    }
  }

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!etapaMenuRef.current) return
      if (!etapaMenuRef.current.contains(event.target as Node)) {
        setIsEtapaMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', onPointerDown)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [])

  return (
    <SimpleModal
      dataBackdrop="static"
      isOpen={isOpen}
      toggle={onToggle}
      size="lg"
      scrollable
      fullscreen="sm"
      header={<SimpleTitleCard>Detalhes do lead</SimpleTitleCard>}
    >
      <ModalContent>
        <LeadHeader>
          <LeadAvatar>{getInitials(modalKanban.informacoes.nome)}</LeadAvatar>
          <LeadInfo>
            <LeadName>{modalKanban.informacoes.nome}</LeadName>
            <LeadPhone>
              {maskPhoneWithDDD(
                (modalKanban.informacoes?.ddd || '00') +
                  (modalKanban.informacoes?.numero || '000000000')
              )}
            </LeadPhone>
          </LeadInfo>
          <LeadHeaderActions>
            {modalKanban.informacoes.situacao && (
              <>
                {modalKanban.informacoes.situacao === 'Em dia' ? (
                  <CardBadge color="#97d399">Em dia</CardBadge>
                ) : (
                  <CardBadge>Atrasado</CardBadge>
                )}
              </>
            )}
          </LeadHeaderActions>

          <LeadHeaderSelect ref={etapaMenuRef}>
            <EtapaSelectContainer>
              <EtapaSelectLabel>
                <BiTransferAlt />
                Mover para
              </EtapaSelectLabel>
              <EtapaSelectTrigger
                type="button"
                onClick={() => setIsEtapaMenuOpen((current) => !current)}
              >
                <EtapaSelectValue>
                  {etapaAtual?.column.title || 'Selecionar etapa'}
                </EtapaSelectValue>
                <BiChevronDown />
              </EtapaSelectTrigger>
              {isEtapaMenuOpen && (
                <EtapaSelectMenu>
                  {kanbanData.columns.map((column) => {
                    const isActive =
                      String(column.id) === String(etapaAtual?.column.id)

                    return (
                      <EtapaSelectOption
                        key={String(column.id)}
                        type="button"
                        $active={isActive}
                        onClick={() => {
                          setIsEtapaMenuOpen(false)
                          onChangeEtapa(String(column.id))
                        }}
                      >
                        <span>{column.title}</span>
                        {isActive && <BiCheck />}
                      </EtapaSelectOption>
                    )
                  })}
                </EtapaSelectMenu>
              )}
            </EtapaSelectContainer>
          </LeadHeaderSelect>
        </LeadHeader>

        <Divider />

        <Section>
          <InfoGridTwoCol>
            <InfoField>
              <InfoFieldLabel>CPF</InfoFieldLabel>
              <InfoFieldValue
                className="pointer"
                onClick={() => {
                  dispatch(
                    atendimentoActions.getFormularioAtendimento30Saga(
                      maskCPF(modalKanban.informacoes.cpf)
                    )
                  )
                }}
              >
                {maskCPF(modalKanban.informacoes.cpf)}
              </InfoFieldValue>
            </InfoField>
            <InfoField>
              <InfoFieldLabel>Consultor</InfoFieldLabel>
              <CustomSelect
                placeholder="Selecione um consultor"
                options={optionsSelect.consultores}
                accessorLabel="descricao"
                accessorValue="id"
                value={modalKanban.consultor.id}
                onChange={(e) => {
                  dispatch(
                    kanbanActions.atribuirConsultorCardKanbanSaga({
                      pipelineId: kanbanData.pipelineId,
                      consultorId: e,
                      clienteId: modalKanban.informacoes.clienteId,
                      pipelineEtapaClienteId:
                        modalKanban.informacoes.pipelineEtapaClienteId
                    })
                  )
                }}
                isLoading={loadingSelect.consultores}
              />
            </InfoField>
            <InfoField>
              <InfoFieldLabel>Profissão</InfoFieldLabel>
              <InfoFieldValue>
                {modalKanban.informacoes.profissao || 'Não informado'}
              </InfoFieldValue>
            </InfoField>
            <InfoField>
              <InfoFieldLabel>Origem</InfoFieldLabel>
              <InfoFieldValue>
                {modalKanban.informacoes.origem || '—'}
              </InfoFieldValue>
            </InfoField>
            <InfoField>
              <InfoFieldLabel>E-mail</InfoFieldLabel>
              <InfoFieldValue>
                {modalKanban.informacoes.email || '—'}
              </InfoFieldValue>
            </InfoField>
            <InfoField>
              <InfoFieldLabel>Atualizado</InfoFieldLabel>
              <InfoFieldValue>
                {modalKanban.informacoes.dataMovimentacao
                  ? moment(modalKanban.informacoes.dataMovimentacao).format(
                      'DD/MM/YYYY HH:mm'
                    )
                  : '—'}
              </InfoFieldValue>
            </InfoField>
          </InfoGridTwoCol>
        </Section>

        <Section>
          <TagsWrapper>
            <TagsLabel>TAGS</TagsLabel>
            {modalKanban.informacoes.perfis?.map((perfil, i) => (
              <Tag key={perfil + i}>{perfil}</Tag>
            ))}
          </TagsWrapper>
        </Section>

        <Divider />

        <ActionButtonsRow>
          <CustomButton
            background="#2563eb"
            borderRadius={25}
            padding="10"
            onClick={() => {
              const { pipelineEtapaClienteId, telefoneId, numero, ddd, cpf } =
                modalKanban.informacoes
              if (!numero || !telefoneId) {
                toast.warning('Cliente sem telefone cadastrado para discagem.')
                return
              }
              dispatch(
                kanbanActions.discarClienteKanbanSaga({
                  pipelineEtapaClienteId,
                  telefoneId,
                  numero: maskPhoneWithDDD((ddd || '') + numero),
                  cpf
                })
              )
            }}
          >
            <BsTelephone className="me-2" />
            Ligar
          </CustomButton>
          <CustomButton
            background="#25d366"
            borderRadius={25}
            padding="10"
            onClick={() => {
              dispatch(kanbanActions.toggleChatwootModal(true))
            }}
          >
            <BsWhatsapp className="me-2" />
            Whatsapp
          </CustomButton>
          <CustomButton background="#f59e0b" borderRadius={25} padding="10">
            <BsEnvelope className="me-2" />
            Email
          </CustomButton>
          <CustomButton
            background="#7c3aed"
            borderRadius={25}
            padding="10"
            onClick={() => {
              dispatch(kanbanActions.setIsOpenModalAgendamento(true))
            }}
          >
            <BsCalendarCheck className="me-2" />
            Agendar
          </CustomButton>
        </ActionButtonsRow>

        <Section>
          <SectionTitle
            className="pointer"
            onClick={() => setAnotacoesIsOpen(!anotacoesIsOpen)}
          >
            Anotações
            {anotacoesIsOpen ? (
              <BiChevronUp className="toggler" />
            ) : (
              <BiChevronDown className="toggler" />
            )}
          </SectionTitle>
          <Collapse isOpen={anotacoesIsOpen}>
            <div className="d-flex flex-column gap-2 mb-3">
              <InputCustom
                type="text"
                placeholder="Digite uma anotação sobre este lead..."
                onChange={(e) => {
                  onChangeAnotacao(e.target.value)
                }}
                value={modalKanban.anotacao}
                invalid={invalidAnotacao}
              />
              <div>
                <CustomButton onClick={criarAnotacao}>
                  Adicionar anotação
                </CustomButton>
              </div>
            </div>
            <div className="d-flex flex-column gap-2">
              {modalKanban.informacoes.anotacoes?.map((anotacao, i) => (
                <HistoricoWrapper key={anotacao.descricao + i}>
                  <InfoArea>
                    <InfoArea>
                      <Title>{anotacao.descricao}</Title>
                      <MetaInfo>
                        <span>{anotacao.nomeResponsavel}</span>
                        <span>
                          {moment(anotacao.dataCriacao).format(
                            'DD/MM/YYYY, HH:mm'
                          )}
                        </span>
                      </MetaInfo>
                    </InfoArea>
                  </InfoArea>
                </HistoricoWrapper>
              ))}
            </div>
          </Collapse>
        </Section>

        <Section className="d-flex flex-column gap-3">
          <SectionTitle
            className="pointer"
            onClick={() => setInteracoesIsOpen(!interacoesIsOpen)}
          >
            Histórico de interações
            {interacoesIsOpen ? (
              <BiChevronUp className="toggler" />
            ) : (
              <BiChevronDown className="toggler" />
            )}
          </SectionTitle>
          <Collapse isOpen={interacoesIsOpen}>
            <div className="d-flex flex-column gap-2">
              {modalKanban.informacoes.iteracoes?.map((iteracao, i) => (
                <HistoricoWrapper key={iteracao.descricao + i}>
                  <InfoArea>
                    <InfoArea>
                      <Title>{iteracao.descricao}</Title>
                      <MetaInfo>
                        <span>{iteracao.nomeResponsavel}</span>
                        <span>
                          {moment(iteracao.dataCriacao).format(
                            'DD/MM/YYYY, HH:mm'
                          )}
                        </span>
                      </MetaInfo>
                    </InfoArea>
                  </InfoArea>
                </HistoricoWrapper>
              ))}
            </div>
          </Collapse>
        </Section>
      </ModalContent>
    </SimpleModal>
  )
}

export default ModalCard
